(() => {
  'use strict';

  const CONTENT_SELECTORS = [
    '.article-template__content.rte',
    'main .page-width--narrow > .rte',
    'main .shopify-section .rte'
  ];

  const IMAGE_RE = /\.(avif|gif|jpe?g|png|svg|webp)$/i;
  const VIDEO_RE = /\.(m4v|mov|mp4|webm)$/i;
  const PDF_RE = /\.pdf$/i;
  const BLOCKED_PROTOCOLS = /^(mailto|tel|sms|javascript):/i;

  let overlay;
  let mediaSlot;
  let captionSlot;
  let closeButton;
  let lastFocusedElement;

  function closestElement(target, selector) {
    if (target instanceof Element) {
      return target.closest(selector);
    }

    return target && target.parentElement ? target.parentElement.closest(selector) : null;
  }

  function cleanPath(url) {
    try {
      return new URL(url, window.location.href).pathname.toLowerCase();
    } catch (error) {
      return String(url || '').split(/[?#]/)[0].toLowerCase();
    }
  }

  function getContentRoots() {
    return CONTENT_SELECTORS
      .flatMap((selector) => Array.from(document.querySelectorAll(selector)))
      .filter((root, index, roots) => roots.indexOf(root) === index);
  }

  function isInsideContentRoot(element) {
    return getContentRoots().some((root) => root.contains(element));
  }

  function linkHasImage(link) {
    return Boolean(link.querySelector('img, picture'));
  }

  function looksLikeUploadedMediaHref(href) {
    try {
      const url = new URL(href, window.location.href);
      const host = url.hostname.toLowerCase();
      const path = url.pathname.toLowerCase();

      return (
        path.includes('/wp-content/uploads/') ||
        path.includes('/cdn/shop/files/') ||
        ((host.includes('cdn.shopify.com') || host.includes('shopifycdn.net')) && path.includes('/files/'))
      );
    } catch (error) {
      const value = String(href || '').toLowerCase();
      return value.includes('/wp-content/uploads/') || value.includes('/cdn/shop/files/') || value.includes('/files/');
    }
  }

  function getMediaType(link) {
    if (!link || !link.href || BLOCKED_PROTOCOLS.test(link.getAttribute('href') || '')) {
      return null;
    }

    if (link.matches('[data-no-lightbox], .no-lightbox')) {
      return null;
    }

    const path = cleanPath(link.href);

    if (PDF_RE.test(path)) {
      return 'pdf';
    }

    if (VIDEO_RE.test(path)) {
      return 'video';
    }

    if (IMAGE_RE.test(path)) {
      return 'image';
    }

    /* Legacy WordPress/Swipebox links and Shopify CDN file links sometimes lose the file
       extension after upload. If a media thumbnail is inside the link, treat it as an image. */
    if (link.classList.contains('swipebox') || (linkHasImage(link) && looksLikeUploadedMediaHref(link.href))) {
      return 'image';
    }

    return null;
  }

  function getCaption(link) {
    const image = link.querySelector('img');
    const values = [
      link.getAttribute('data-caption'),
      link.getAttribute('title'),
      link.getAttribute('aria-label'),
      image ? image.getAttribute('alt') : '',
      link.textContent
    ];

    const caption = values
      .map((value) => (value || '').replace(/\s+/g, ' ').trim())
      .find(Boolean);

    return caption || '';
  }

  function buildOverlay() {
    if (overlay) {
      return;
    }

    overlay = document.createElement('div');
    overlay.className = 'fd-content-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Media preview');
    overlay.innerHTML = `
      <div class="fd-content-lightbox__backdrop" data-fd-lightbox-close></div>
      <div class="fd-content-lightbox__dialog" role="document">
        <button class="fd-content-lightbox__close" type="button" aria-label="Close media preview" data-fd-lightbox-close>&times;</button>
        <div class="fd-content-lightbox__media"></div>
        <div class="fd-content-lightbox__caption" aria-live="polite"></div>
      </div>
    `;

    document.body.appendChild(overlay);
    mediaSlot = overlay.querySelector('.fd-content-lightbox__media');
    captionSlot = overlay.querySelector('.fd-content-lightbox__caption');
    closeButton = overlay.querySelector('.fd-content-lightbox__close');

    overlay.addEventListener('click', (event) => {
      if (closestElement(event.target, '[data-fd-lightbox-close]')) {
        closeLightbox();
      }
    });
  }

  function openMedia(source, type, caption = '') {
    buildOverlay();

    lastFocusedElement = document.activeElement;
    mediaSlot.replaceChildren();

    let mediaElement;

    if (type === 'video') {
      mediaElement = document.createElement('video');
      mediaElement.src = source;
      mediaElement.controls = true;
      mediaElement.autoplay = true;
      mediaElement.playsInline = true;
      mediaElement.setAttribute('playsinline', '');
      mediaElement.preload = 'metadata';
    } else {
      mediaElement = document.createElement('img');
      mediaElement.src = source;
      mediaElement.alt = caption || 'Expanded image';
      mediaElement.decoding = 'async';
    }

    mediaSlot.appendChild(mediaElement);
    captionSlot.textContent = caption;
    overlay.classList.add('is-open');
    document.body.classList.add('fd-lightbox-lock');
    closeButton.focus({ preventScroll: true });
  }

  function openLightbox(link, type) {
    openMedia(link.href, type, getCaption(link));
  }

  function closeLightbox() {
    if (!overlay || !overlay.classList.contains('is-open')) {
      return;
    }

    overlay.classList.remove('is-open');
    document.body.classList.remove('fd-lightbox-lock');
    mediaSlot.replaceChildren();
    captionSlot.textContent = '';

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus({ preventScroll: true });
    }
  }

  function annotateLinks() {
    getContentRoots().forEach((root) => {
      root.querySelectorAll('a[href]').forEach((link) => {
        const type = getMediaType(link);

        if (type === 'image' || type === 'video') {
          link.classList.add('fd-lightbox-media-link');
          link.setAttribute('data-fd-lightbox-type', type);
        }

        if (type === 'pdf') {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
        }
      });

      root.querySelectorAll('img').forEach((image) => {
        if (!image.closest('a')) {
          image.classList.add('fd-lightbox-standalone-image');
          image.setAttribute('tabindex', '0');
          image.setAttribute('role', 'button');
          image.setAttribute('aria-label', image.getAttribute('alt') || 'Open image preview');
        }
      });
    });
  }

  function handleDocumentClick(event) {
    const link = closestElement(event.target, 'a[href]');

    if (!link) {
      const image = closestElement(event.target, 'img.fd-lightbox-standalone-image');

      if (image && isInsideContentRoot(image)) {
        event.preventDefault();
        openMedia(image.currentSrc || image.src, 'image', image.getAttribute('alt') || '');
      }

      return;
    }

    if (!isInsideContentRoot(link)) {
      return;
    }

    const type = getMediaType(link);

    if (type === 'pdf') {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      return;
    }

    if (type !== 'image' && type !== 'video') {
      return;
    }

    event.preventDefault();
    openLightbox(link, type);
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      closeLightbox();
      return;
    }

    const image = event.target instanceof Element && event.target.matches('img.fd-lightbox-standalone-image')
      ? event.target
      : null;

    if ((event.key === 'Enter' || event.key === ' ') && image) {
      event.preventDefault();
      openMedia(image.currentSrc || image.src, 'image', image.getAttribute('alt') || '');
    }
  }

  function init() {
    if (!getContentRoots().length) {
      return;
    }

    annotateLinks();
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('shopify:section:load', annotateLinks);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
